import { useMemo, useState } from "react";
import { CheckIcon, UserCogIcon } from "lucide-react";

import { OwnerAvatars, Row, Text } from "@/components";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import {
  mergeUserOptionsWithSelected,
  useTaggableUsers,
} from "@/contexts/TaggableUsersContext";
import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { useUpdateSetJobManager } from "@/hooks/updateSets/useUpdateSetJobManager";
import { ADMIN_USER_IDS } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";

// Job managers oversee a job — it lands on their dashboard — without owning its
// reqs or candidacies. Everyone sees who's overseeing; only leadership edits.
export const JobManagersPopover = ({
  jobId,
  managerUserIds,
}: {
  jobId: string;
  managerUserIds: number[];
}) => {
  /* Database CRUD Operations */
  // Update
  const { mutate: updateJobManager } = useUpdateSetJobManager();

  /* Context */
  const currentUser = useGetCurrentUser();
  const { allCurrentEmployees, userInitialsMap, userNameMap } =
    useTaggableUsers();

  /* React Hooks */
  const [isOpen, setIsOpen] = useState(false);

  // Same people who can be @mentioned, alphabetical.
  const userOptions = useMemo(
    () =>
      mergeUserOptionsWithSelected(
        allCurrentEmployees
          .map((user) => ({
            value: user.id,
            label: `${user.firstName} ${user.lastName}`,
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
        userNameMap,
        managerUserIds,
      ),
    [allCurrentEmployees, userNameMap, managerUserIds],
  );

  /* Helpers */
  const canEdit = ADMIN_USER_IDS.includes(currentUser?.id ?? 0);
  const tooltipLabel =
    managerUserIds.length > 0
      ? `Job Managers · ${managerUserIds.map((userId) => userNameMap[userId] ?? "Unknown").join(", ")}`
      : "Job Managers";

  /* Handlers */
  const handleSelect = (userId: number) => {
    const isManager = !managerUserIds.includes(userId);
    mixpanelUtils.trackButtonClick(
      isManager ? "Add job manager" : "Remove job manager",
      MIXPANEL_SCREENS.JOB,
    );
    updateJobManager({ jobId, userId, isManager });
  };

  /* Render */
  const renderTrigger = () => (
    // Raw <div> because Row doesn't forward refs, which Radix asChild requires.
    <div
      className={cn(
        "flex h-8 items-center gap-1.5 rounded-full border border-slate-200 px-2.5",
        canEdit ? "cursor-pointer hover:bg-slate-50" : "cursor-default",
      )}
    >
      <UserCogIcon className="h-3.5 w-3.5 text-slate-500" />
      {managerUserIds.length > 0 ? (
        <OwnerAvatars
          ownerUserIds={managerUserIds}
          userInitialsMap={userInitialsMap}
        />
      ) : null}
    </div>
  );

  const renderWithTooltip = (trigger: React.ReactNode) => (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent>{tooltipLabel}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  const render = () => {
    if (!canEdit) {
      return renderWithTooltip(renderTrigger());
    }

    return (
      <Popover onOpenChange={setIsOpen} open={isOpen}>
        {renderWithTooltip(
          <PopoverTrigger asChild>{renderTrigger()}</PopoverTrigger>,
        )}
        <PopoverContent align="end" className="w-64 p-0">
          <Command>
            <CommandInput placeholder="Search users..." />
            <CommandList>
              <CommandEmpty>
                <Text className="text-sm font-normal text-slate-500">
                  No users found.
                </Text>
              </CommandEmpty>
              <CommandGroup>
                {userOptions.map((option) => (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      handleSelect(option.value);
                    }}
                  >
                    <Row className="items-center gap-2">
                      <CheckIcon
                        className={cn(
                          "h-4 w-4",
                          !managerUserIds.includes(option.value) && "invisible",
                        )}
                      />
                      {option.label}
                    </Row>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  };

  return render();
};
